import { CodePushDialogManager, type CustomDialogConfig } from "./CodePushDialogManager";
import { Box } from "../../../mads";
import { Dialog } from "../../../mads/Dialog";
import { Spinner } from "../../../mads/Spinner";
import { useI18n } from "../../I18n";

import React, { useCallback, useEffect, useState } from "react";
import { StyleSheet } from "react-native";

/**
 * CodePushDialog - Renders OTA update dialog using mads Dialog component.
 * Subscribes to CodePushDialogManager for dialog state.
 */
export function CodePushDialog() {
  const i18n = useI18n();
  const [dialogConfig, setDialogConfig] = useState<CustomDialogConfig | null>(
    CodePushDialogManager.getConfig(),
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadMessage, setDownloadMessage] = useState("");

  useEffect(() => {
    return CodePushDialogManager.subscribe((config) => {
      setDialogConfig(config);
      if (config) {
        setIsDownloading(false);
        setDownloadMessage("");
      }
    });
  }, []);

  const handleInstall = useCallback(() => {
    if (dialogConfig) {
      setDownloadMessage(i18n.gettext("Installing update..."));
      setIsDownloading(true);
      dialogConfig.onInstall();
    }
  }, [dialogConfig, i18n]);

  const handleDismiss = useCallback(() => {
    if (dialogConfig) {
      CodePushDialogManager.hide();
      dialogConfig.onIgnore();
    }
  }, [dialogConfig]);

  if (!dialogConfig) {
    return null;
  }

  if (isDownloading) {
    return (
      <Dialog
        visible={true}
        onClose={() => {}}
        headerText={dialogConfig.title}
        image={
          <Box sx={styles.spinnerContainer}>
            <Spinner accessibilityLabel={downloadMessage} />
          </Box>
        }
        subtitle={downloadMessage}
      />
    );
  }

  const buttons = [
    {
      children: dialogConfig.installButtonText,
      onPress: handleInstall,
      variant: "primary" as const,
    },
  ];

  return (
    <Dialog
      visible={true}
      onClose={handleDismiss}
      headerText={dialogConfig.title}
      subtitle={dialogConfig.message}
      buttons={buttons}
    />
  );
}

const styles = StyleSheet.create({
  spinnerContainer: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
  },
});
